import { useCallback, useId, useMemo, useState } from 'react';
import { z } from 'zod';

import { inputToCents } from '@/lib/money';

/**
 * Form validation for the admin.
 *
 * Every form that saves data describes its draft with a Zod schema and checks it
 * through `useValidation`. The schema mirrors what the API accepts, so a form
 * never sends something the server will reject, and the message a person sees
 * names the field in their words rather than the API's.
 *
 * Timing follows the design handoff: a field is checked when you leave it, never
 * on the first keystroke. Once it has been left (or Save has been pressed) it is
 * re-checked as you type, so a fixed field clears straight away. Save runs
 * `check()`, which reveals every error and moves focus to the first bad field.
 */

/** What `check()` may move focus to. */
const FOCUSABLE =
  'input:not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [tabindex]';

/** Dotted paths into the draft: `name`, `categories.0.items.2.price`. */
export type Errors = Record<string, string>;

/** The first message per path, which is all a field shows. */
export function errorsOf(schema: z.ZodType, value: unknown): Errors {
  const result = schema.safeParse(value);
  if (result.success) return {};
  const errors: Errors = {};
  for (const issue of result.error.issues) {
    const path = issue.path.join('.');
    if (!(path in errors)) errors[path] = issue.message;
  }
  return errors;
}

export function useValidation(schema: z.ZodType, value: unknown) {
  const [touched, setTouched] = useState<ReadonlySet<string>>(() => new Set());
  const [submitted, setSubmitted] = useState(false);
  const errors = useMemo(() => errorsOf(schema, value), [schema, value]);
  const valid = Object.keys(errors).length === 0;

  /**
   * The message to show for a path, once it has been left or Save pressed. With
   * `within`, a problem anywhere under the path counts too — a list such as
   * tags shows the error for `tags.3` against the whole list.
   */
  const error = useCallback(
    (path: string, { within = false }: { within?: boolean } = {}) => {
      if (!submitted && !touched.has(path)) return undefined;
      if (errors[path] || !within) return errors[path];
      const key = Object.keys(errors).find((k) => k.startsWith(`${path}.`));
      return key ? errors[key] : undefined;
    },
    [errors, submitted, touched],
  );

  const touch = useCallback(
    (path: string) => setTouched((prev) => (prev.has(path) ? prev : new Set(prev).add(path))),
    [],
  );

  /** Spread onto a `<Field>`: its error, and the blur that reveals it. */
  const field = useCallback(
    (path: string, options?: { within?: boolean }) => ({
      error: error(path, options),
      onBlur: () => touch(path),
    }),
    [error, touch],
  );

  /**
   * Call before saving. Shows every error, and when there is one moves focus to
   * the first field marked invalid once React has drawn it. Returns whether the
   * draft may be sent.
   */
  const check = useCallback(() => {
    setSubmitted(true);
    if (valid) return true;
    requestAnimationFrame(() => {
      // An open dialog is the form being saved, so look there before the page
      // behind it.
      const scope = document.querySelector<HTMLElement>('[role="dialog"]') ?? document;
      // The mark can land on a wrapper — a Field whose child is a row of
      // controls — so focus the first control inside it when it cannot take
      // focus itself.
      const marked = scope.querySelector<HTMLElement>('[aria-invalid="true"]');
      const target = marked?.matches(FOCUSABLE)
        ? marked
        : (marked?.querySelector<HTMLElement>(FOCUSABLE) ?? marked);
      if (target) return target.focus();
      // A rule about a whole list (too many lines, a question count) has no one
      // control to blame, so bring its message into view instead.
      scope.querySelector('[data-field-error]')?.scrollIntoView({ block: 'center' });
    });
    return false;
  }, [valid]);

  /** Forget what was touched, for a form that is closed or reloaded. */
  const reset = useCallback(() => {
    setTouched(new Set());
    setSubmitted(false);
  }, []);

  return { errors, valid, error, touch, field, check, reset };
}

/** What a form section takes to wire its fields to its parent's validation. */
export type FieldBinder = ReturnType<typeof useValidation>['field'];

/**
 * Validation for a control with no <Field> round it — a table cell under a
 * column heading, or an input its row already names.
 *
 * Returns what Field would hand the control (`props`, spread onto it) and the
 * message to draw beneath it (`message`, spread onto `InlineError`). The
 * control itself carries `aria-invalid`, so Save's `check()` focuses it.
 */
export function useInlineField(field: FieldBinder) {
  const id = useId();
  return (path: string, options?: { within?: boolean }) => {
    const { error, onBlur } = field(path, options);
    const errorId = `${id}-${path}`;
    return {
      props: {
        'aria-invalid': error ? true : undefined,
        'aria-describedby': error ? errorId : undefined,
        onBlur,
      },
      message: { id: errorId, error },
    };
  };
}

/* ------------------------------------------------------------------ rules --- */
/*
 * Building blocks with the admin's wording. Schemas compose these rather than
 * writing messages inline, so the same mistake reads the same on every form.
 */

export const REQUIRED = 'Required.';

const tooLong = (max: number) => `Keep it to ${max.toLocaleString()} characters or fewer.`;

/** Free text that may be left blank. */
export const optionalText = (max: number) => z.string().trim().max(max, tooLong(max));

/** Free text that must be filled in. */
export const requiredText = (max: number) =>
  z.string().trim().min(1, REQUIRED).max(max, tooLong(max));

const EMAIL = 'Enter a valid email address, like name@example.com.';
const isEmail = (value: string) => z.email().safeParse(value).success;

export const requiredEmail = z
  .string()
  .trim()
  .min(1, REQUIRED)
  .max(254, tooLong(254))
  .refine(isEmail, EMAIL);

export const optionalEmail = z
  .string()
  .trim()
  .max(254, tooLong(254))
  .refine((v) => v === '' || isEmail(v), `${EMAIL} Or leave it blank.`);

/**
 * The API takes a phone number as any text up to 50 characters, and people
 * write them every way — "03/123456", "+961 70 111 222 ext. 4" — so only the
 * length is checked.
 */
export const requiredPhone = requiredText(50);
export const optionalPhone = optionalText(50);

/**
 * A dollar amount typed as text ("12.50"), as money inputs keep it until save.
 * `min` and `max` are in cents.
 */
export const dollars = ({ min = 0, max }: { min?: number; max?: number } = {}) =>
  z
    .string()
    .trim()
    .min(1, REQUIRED)
    .refine((v) => inputToCents(v) !== null, 'Enter an amount in dollars, like 12.50.')
    .refine(
      (v) => (inputToCents(v) ?? 0) >= min,
      min === 1 ? 'Enter an amount above zero.' : `Enter at least $${(min / 100).toFixed(2)}.`,
    )
    .refine(
      (v) => max === undefined || (inputToCents(v) ?? 0) <= max,
      `Enter no more than $${((max ?? 0) / 100).toFixed(2)}.`,
    );

/** A whole number typed as text, within bounds. */
export const wholeNumberText = ({ min, max }: { min: number; max: number }) =>
  z
    .string()
    .trim()
    .min(1, REQUIRED)
    .regex(/^\d+$/, 'Enter a whole number.')
    .refine((v) => Number(v) >= min, `Enter ${min} or more.`)
    .refine((v) => Number(v) <= max, `Enter ${max.toLocaleString()} or less.`);

/** A whole number held as a number, within bounds. */
export const wholeNumber = ({ min, max }: { min: number; max: number }) =>
  z
    .number({ error: 'Enter a whole number.' })
    .refine((n) => Number.isFinite(n), 'Enter a whole number.')
    .refine((n) => Number.isInteger(n), 'Enter a whole number.')
    .refine((n) => n >= min, `Enter ${min} or more.`)
    .refine((n) => n <= max, `Enter ${max.toLocaleString()} or less.`);

/**
 * A calendar date from a date input (YYYY-MM-DD). Round-tripped, because
 * `Date` quietly rolls 2026-02-30 over to March rather than refusing it.
 */
export const isoDate = z
  .string()
  .min(1, REQUIRED)
  .refine((v) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
    const parsed = new Date(`${v}T00:00:00Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === v;
  }, 'Enter a valid date.');

/** A time from a time input (HH:MM). */
export const clockTime = z
  .string()
  .min(1, REQUIRED)
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Enter a valid time.');

/** A choice from a select, where the empty value means nothing was picked. */
export const picked = (message = 'Choose one.') => z.string().min(1, message);
